import React from 'react';
import { BarChart, Trash2, Layers, Calendar, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для отслеживания прогресса снижения веса
 */
const WeightProgressSidebar = ({ 
  savedProfiles, 
  deleteSavedProfile, 
  loadSavedProfile,
  currentResults
}) => {
  const [expanded, setExpanded] = React.useState(false);
  
  // Если нет сохраненных профилей
  if (!savedProfiles || savedProfiles.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Отслеживание веса
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты для отслеживания вашего прогресса.
          Рассчитайте свои потребности в калориях и сохраните профиль, чтобы начать отслеживание.
        </p>
        
        {currentResults && (
          <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Сохраните текущий расчет для того, чтобы отслеживать прогресс снижения веса.
            </p>
          </div>
        )}
      </div>
    );
  }

  // Получаем последние 5 профилей
  const recentProfiles = [...savedProfiles].sort((a, b) => {
    return new Date(b.date) - new Date(a.date);
  }).slice(0, expanded ? undefined : 3);
  
  // Функция для получения разницы между профилями в калориях и весе
  const getProfileDifference = (profile, previousProfile) => {
    if (!previousProfile) return { calories: 0, weight: 0 };
    
    const calorieDiff = profile.results.dailyCalories - previousProfile.results.dailyCalories;
    const weightDiff = parseFloat(profile.formData.weight) - parseFloat(previousProfile.formData.weight);
    
    return { calories: calorieDiff, weight: weightDiff };
  };
  
  // Определение текущего прогресса к цели (только для целей снижения веса)
  const calculateProgress = () => {
    if (savedProfiles.length < 2) return null;
    
    const firstProfile = savedProfiles.reduce((earliest, profile) => {
      return new Date(profile.date) < new Date(earliest.date) ? profile : earliest;
    }, savedProfiles[0]);
    
    const latestProfile = savedProfiles.reduce((latest, profile) => {
      return new Date(profile.date) > new Date(latest.date) ? profile : latest;
    }, savedProfiles[0]);
    
    // Если нет целевого веса, или это не цель снижения веса, то не показываем прогресс
    if (!firstProfile.results.goalWeight || !firstProfile.formData.goal.includes('loss')) {
      return null;
    }
    
    const initialWeight = parseFloat(firstProfile.formData.weight);
    const currentWeight = parseFloat(latestProfile.formData.weight);
    const targetWeight = firstProfile.results.goalWeight;
    
    // Если нет снижения, то не показываем прогресс
    if (initialWeight <= currentWeight) return null;
    
    const weightToLose = initialWeight - targetWeight;
    const weightLost = initialWeight - currentWeight;
    const progressPercent = Math.min(100, (weightLost / weightToLose) * 100);
    
    return {
      initialWeight,
      currentWeight,
      targetWeight,
      weightToLose,
      weightLost,
      progressPercent,
      remainingWeight: targetWeight < currentWeight ? currentWeight - targetWeight : 0
    };
  };
  
  const progress = calculateProgress();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-emerald-500" />
          Отслеживание веса
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProfiles.length} {formatProfilesCount(savedProfiles.length)}
        </span>
      </div>

      {/* Прогресс по снижению веса (если есть) */}
      {progress && (
        <div className="mb-5 p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
          <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2 flex items-center">
            <TrendingDown className="w-4 h-4 mr-1" />
            Прогресс снижения веса
          </h4>
          
          <div className="flex justify-between text-xs text-emerald-600 dark:text-emerald-400 mb-1">
            <span>Начальный: {progress.initialWeight} кг</span>
            <span>Цель: {progress.targetWeight} кг</span>
          </div>
          
          <div className="w-full h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full mb-2">
            <div 
              className="h-full bg-emerald-500 rounded-full" 
              style={{ width: `${progress.progressPercent}%` }}
            ></div>
          </div>
          
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="bg-white dark:bg-gray-800 p-2 rounded border border-emerald-100 dark:border-emerald-800">
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatNumber(progress.weightLost, 1)} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Сброшено</div>
            </div>
            <div className="bg-white dark:bg-gray-800 p-2 rounded border border-emerald-100 dark:border-emerald-800">
              <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatNumber(progress.remainingWeight, 1)} кг
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Осталось</div>
            </div>
          </div>
        </div>
      )}

      {/* История сохраненных профилей */}
      <div className="space-y-3">
        {recentProfiles.map((profile, index) => {
          const previousProfile = index < savedProfiles.length - 1 ? savedProfiles[index + 1] : null;
          const diff = getProfileDifference(profile, previousProfile);
          
          return (
            <div 
              key={profile.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                    {profile.name}
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center mt-0.5">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(profile.date).toLocaleDateString('ru-RU', { 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Layers className="w-3.5 h-3.5" />}
                    onClick={() => loadSavedProfile(profile.id)}
                    title="Загрузить параметры"
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
                    onClick={() => deleteSavedProfile(profile.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Вес:</span>
                  <span className="font-medium">
                    {profile.formData.weight} кг
                    {diff.weight !== 0 && previousProfile && (
                      <span 
                        className={`ml-1 ${diff.weight < 0 ? 'text-emerald-500' : 'text-red-500'}`}
                      >
                        {diff.weight > 0 ? '+' : ''}{formatNumber(diff.weight, 1)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Калории:</span>
                  <span className="font-medium">
                    {formatNumber(profile.results.dailyCalories, 0)}
                    {diff.calories !== 0 && previousProfile && (
                      <span 
                        className={`ml-1 ${diff.calories < 0 ? 'text-amber-500' : 'text-blue-500'}`}
                      >
                        {diff.calories > 0 ? '+' : ''}{formatNumber(diff.calories, 0)}
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Кнопка показать больше/меньше */}
      {savedProfiles.length > 3 && (
        <button
          className="w-full text-sm text-emerald-600 dark:text-emerald-400 mt-3 py-1 flex items-center justify-center hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <>
              Показать меньше <ChevronUp className="w-4 h-4 ml-1" />
            </>
          ) : (
            <>
              Показать все {savedProfiles.length} {formatProfilesCount(savedProfiles.length)} <ChevronDown className="w-4 h-4 ml-1" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слова "профиль" в зависимости от числа
const formatProfilesCount = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'профилей';
  if (lastDigit === 1) return 'профиль';
  if (lastDigit >= 2 && lastDigit <= 4) return 'профиля';
  return 'профилей';
};

export default WeightProgressSidebar;